import React, { useCallback, useContext, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { writeWarnings } from "../lib/reports";

// App-wide actions available to every screen:
//  toast(message, {error, icon, action: {label, run}})
//  openLog(prefill)        Log a touchpoint modal (contact and/or company)
//  openEditTouch(touch)    the same modal on an existing touch
//  openCreate(kind, prefill)  New person / New company
//  openPalette()
//  openLead(prefill)       New lead ({company, contact, touch, signal})
//  openSignal(prefill)     Add signal ({company}) or edit one ({signal})
//  version / bump()        a counter screens watch to reload after writes
//  noteWarnings(res)       non-blocking notice when a write returns
//                          "warnings" (special-category guard, E9)
const ShellContext = React.createContext(null);

export const useShell = () => useContext(ShellContext);

let toastSeq = 0;

export function ShellProvider({ children }) {
    const [toasts, setToasts] = useState([]);
    const [logState, setLogState] = useState(null); // {prefill} or {touch}
    const [createState, setCreateState] = useState(null); // {kind, prefill}
    const [paletteOpen, setPaletteOpen] = useState(false);
    const [leadState, setLeadState] = useState(null); // {prefill}
    const [signalState, setSignalState] = useState(null); // {prefill}
    const [version, setVersion] = useState(0);
    const timers = useRef({});

    const dismissToast = useCallback((id) => {
        setToasts((t) => t.filter((x) => x.id !== id));
        clearTimeout(timers.current[id]);
    }, []);

    const toast = useCallback(
        (message, opts = {}) => {
            const id = ++toastSeq;
            setToasts((t) => [...t.slice(-2), { id, message, ...opts }]);
            timers.current[id] = setTimeout(() => dismissToast(id), opts.action ? 6000 : opts.error ? 5000 : 2600);
        },
        [dismissToast]
    );

    const bump = useCallback(() => setVersion((v) => v + 1), []);

    const history = useHistory();
    const noteWarnings = useCallback(
        (res) => {
            if (writeWarnings(res).length === 0) return false;
            // Shown after the success toast so both stay visible.
            setTimeout(
                () =>
                    toast("The text may contain special-category data (health, religion, politics…). Keep only what is needed.", {
                        warning: true,
                        icon: "ri-shield-keyhole-line",
                        action: { label: "Keywords", run: () => history.push("/settings/privacy") },
                    }),
                0
            );
            return true;
        },
        [toast, history]
    );

    const value = useMemo(
        () => ({
            toast,
            dismissToast,
            toasts,
            version,
            bump,
            noteWarnings,
            logState,
            openLog: (prefill = {}) => setLogState({ prefill }),
            openEditTouch: (touch) => setLogState({ touch }),
            closeLog: () => setLogState(null),
            createState,
            openCreate: (kind, prefill = {}) => setCreateState({ kind, prefill }),
            closeCreate: () => setCreateState(null),
            paletteOpen,
            openPalette: () => setPaletteOpen(true),
            closePalette: () => setPaletteOpen(false),
            togglePalette: () => setPaletteOpen((o) => !o),
            leadState,
            openLead: (prefill = {}) => setLeadState({ prefill }),
            closeLead: () => setLeadState(null),
            signalState,
            openSignal: (prefill = {}) => setSignalState({ prefill }),
            closeSignal: () => setSignalState(null),
        }),
        [toast, dismissToast, toasts, version, bump, noteWarnings, logState, createState, paletteOpen, leadState, signalState]
    );

    return <ShellContext.Provider value={value}>{children}</ShellContext.Provider>;
}

export function Toasts() {
    const { toasts, dismissToast } = useShell();
    if (!toasts.length) return null;
    return (
        <div className="hz-toasts" aria-live="polite">
            {toasts.map((t) => (
                <div key={t.id} className={"hz-toast" + (t.error ? " error" : t.warning ? " warning" : "")}>
                    <i className={t.icon || (t.error ? "ri-error-warning-line" : "ri-check-line")}></i>
                    <span>{t.message}</span>
                    {t.action && (
                        <button
                            type="button"
                            onClick={() => {
                                dismissToast(t.id);
                                t.action.run();
                            }}
                        >
                            {t.action.label}
                        </button>
                    )}
                </div>
            ))}
        </div>
    );
}
